/**
 * Give Trial Dialog — lane-aware operator trial granting.
 * Extension lane: plan + interval + days + target member (personal, user-lane grant).
 * TMS lane: tier + days (application-scoped).
 * Trials are Stripe-driven: when the trial ends the customer auto-downgrades to Free.
 */
import React, { useEffect, useMemo, useState } from 'react'
import { Modal, ModalDialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@mui/joy'
import { BillingPeriod, SubscriptionPlan, TmsPaidTier, TmsTier } from '@numeo/types-core'
import { billingApi } from './api'
import { CustomerUser, TmsSubscriptionInfo } from './types'

type TrialLane = 'extension' | 'tms'

interface GiveTrialDialogProps {
    open: boolean
    lane: TrialLane
    appId: string
    /** Extension lane — members selectable as the trial target. */
    users?: CustomerUser[]
    /** Extension lane — app-member id preselected as target (usually the owner). */
    defaultUserId?: string
    /** Extension lane — owner email; the owner may be targeted without a standaloneUserId (server derives it). */
    ownerEmail?: string
    /** TMS lane — current TMS subscription, used for the active-paid guard. */
    tmsSubscription?: TmsSubscriptionInfo | null
    onClose: () => void
    onSuccess: (message: string) => void
    /** Called after a successful grant so the parent can refetch the customer detail. */
    onRefresh: () => Promise<void>
}

const PAID_STATUSES = ['active', 'past_due']

/**
 * Mirrors the server's assertTrialGrantAllowed: paid coverage can come from the member's
 * EFFECTIVE plan (personal sub, team seat, or app lane — best-of, computed server-side).
 * A trialing effective plan stays grantable; any paid active/past_due lane blocks.
 */
function isActivePaidExtension(user: CustomerUser | undefined): boolean {
    if (user?.effective) {
        return PAID_STATUSES.includes(user.effective.status) && user.effective.plan !== SubscriptionPlan.FREE
    }
    const sub = user?.extensionSubscription
    if (!sub) return false
    return PAID_STATUSES.includes(sub.status) && sub.plan !== SubscriptionPlan.FREE
}

function isActivePaidTms(tms: TmsSubscriptionInfo | null | undefined): boolean {
    if (!tms) return false
    return tms.source === 'paid' && PAID_STATUSES.includes(tms.status)
}

export default function GiveTrialDialog({
    open,
    lane,
    appId,
    users,
    defaultUserId,
    ownerEmail,
    tmsSubscription,
    onClose,
    onSuccess,
    onRefresh,
}: GiveTrialDialogProps) {
    const [plan, setPlan] = useState<SubscriptionPlan.PRO | SubscriptionPlan.ULTRA>(SubscriptionPlan.PRO)
    const [trialInterval, setTrialInterval] = useState<BillingPeriod>('monthly')
    const [tier, setTier] = useState<TmsPaidTier>(TmsTier.Pro)
    const [days, setDays] = useState('14')
    const [targetUserId, setTargetUserId] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [serverError, setServerError] = useState<string | null>(null)

    // Reset to defaults every time the dialog opens so stale selections never leak between grants.
    useEffect(() => {
        if (!open) return
        setPlan(SubscriptionPlan.PRO)
        setTrialInterval('monthly')
        setTier(TmsTier.Pro)
        setDays('14')
        setTargetUserId(defaultUserId || users?.[0]?.userId || '')
        setServerError(null)
    }, [open, defaultUserId, users])

    const targetUser = useMemo(() => users?.find((u) => u.userId === targetUserId), [users, targetUserId])
    const isOwnerTarget = !!targetUser && !!ownerEmail && targetUser.email === ownerEmail

    const daysNum = parseInt(days, 10)
    const daysValid = Number.isFinite(daysNum) && daysNum >= 1 && daysNum <= 365

    // Hard product rule (TRIAL_GRANTS_SPEC): never grant a trial over a live paid subscription.
    // The server 409s anyway — this just tells the operator why before they try.
    const paidBlocked = lane === 'extension' ? isActivePaidExtension(targetUser) : isActivePaidTms(tmsSubscription)
    // A member's personal grant is addressed by their STANDALONE user id; the owner is the only
    // target the server can derive by itself when that id is missing.
    const missingStandalone = lane === 'extension' && !!targetUser && !targetUser.standaloneUserId && !isOwnerTarget
    const canSubmit = daysValid && !paidBlocked && !submitting && (lane === 'tms' || (!!targetUser && !missingStandalone))

    const handleClose = () => {
        if (submitting) return
        onClose()
    }

    const handleSubmit = async () => {
        if (!canSubmit) return
        setSubmitting(true)
        setServerError(null)
        try {
            if (lane === 'extension') {
                await billingApi.assignPlan(appId, { plan, trialDays: daysNum, userId: targetUser?.standaloneUserId || undefined, interval: trialInterval })
                onSuccess(`${plan} trial (${trialInterval}) granted to ${targetUser?.email || 'owner'} for ${daysNum} days`)
            } else {
                const result = await billingApi.grantTmsTrial(appId, { tier, trialDays: daysNum, interval: trialInterval })
                const featuresNote = result.featuresEnabled === false ? ' — WARNING: TMS features could not be enabled on the app, check manually' : ''
                onSuccess(`TMS ${result.tier} trial ${result.extended ? 'extended' : 'granted'} for ${daysNum} days${featuresNote}`)
            }
            onClose()
            await onRefresh()
        } catch (err: unknown) {
            const axiosErr = err as { response?: { data?: { message?: string } } }
            setServerError(axiosErr.response?.data?.message || 'Failed to grant trial')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Modal open={open} onClose={handleClose}>
            <ModalDialog variant="outlined" sx={{ maxWidth: 480, width: '100%' }}>
                <DialogTitle>{lane === 'extension' ? 'Give Extension Trial' : 'Give TMS Trial'}</DialogTitle>
                <DialogContent>
                    <div className="space-y-4 pt-1">
                        {lane === 'extension' && users && (
                            <div>
                                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Target member</label>
                                <select
                                    value={targetUserId}
                                    onChange={(e) => setTargetUserId(e.target.value)}
                                    disabled={submitting}
                                    className="w-full h-9 text-sm px-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                >
                                    {users.map((u) => (
                                        <option key={u.userId} value={u.userId}>
                                            {u.email}
                                            {ownerEmail && u.email === ownerEmail ? ' (owner)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        {lane === 'extension' ? (
                            <div className="flex flex-wrap items-end gap-4">
                                <div>
                                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Plan</label>
                                    <div className="flex gap-1.5">
                                        {([SubscriptionPlan.PRO, SubscriptionPlan.ULTRA] as const).map((p) => (
                                            <button
                                                key={p}
                                                onClick={() => setPlan(p)}
                                                disabled={submitting}
                                                className={`h-8 px-4 text-xs font-bold rounded-md transition-all ${plan === p ? (p === SubscriptionPlan.PRO ? 'bg-blue-600 text-white shadow-sm' : 'bg-violet-600 text-white shadow-sm') : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`}
                                            >
                                                {p}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Interval</label>
                                    <div className="flex gap-1.5">
                                        {(['monthly', 'annual'] as BillingPeriod[]).map((i) => (
                                            <button
                                                key={i}
                                                onClick={() => setTrialInterval(i)}
                                                disabled={submitting}
                                                className={`h-8 px-3 text-xs font-medium rounded-md capitalize transition-all ${trialInterval === i ? 'bg-gray-800 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`}
                                            >
                                                {i}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Tier</label>
                                    <div className="flex gap-1.5">
                                        {([TmsTier.Basic, TmsTier.Pro] as TmsPaidTier[]).map((t) => (
                                            <button
                                                key={t}
                                                onClick={() => setTier(t)}
                                                disabled={submitting}
                                                className={`h-8 px-4 text-xs font-bold rounded-md uppercase transition-all ${tier === t ? 'bg-cyan-700 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`}
                                            >
                                                {t}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Interval</label>
                                    <div className="flex gap-1.5">
                                        {(['monthly', 'annual'] as BillingPeriod[]).map((i) => (
                                            <button
                                                key={i}
                                                onClick={() => setTrialInterval(i)}
                                                disabled={submitting}
                                                className={`h-8 px-3 text-xs font-medium rounded-md capitalize transition-all ${trialInterval === i ? 'bg-gray-800 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`}
                                            >
                                                {i}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                        <div>
                            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Trial length (days)</label>
                            <div className="flex items-center gap-1.5">
                                <input
                                    type="number"
                                    min={1}
                                    max={365}
                                    value={days}
                                    onChange={(e) => setDays(e.target.value)}
                                    disabled={submitting}
                                    className="h-8 w-24 text-sm px-2 bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                />
                                <span className="text-xs text-gray-500">days (1–365)</span>
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                            Trial ends → customer automatically downgrades to Free (Stripe-driven)
                        </p>
                        {paidBlocked && (
                            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                                Customer has an active paid subscription — trial grants are not allowed
                            </p>
                        )}
                        {missingStandalone && (
                            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                                This member has no standalone user record yet — a personal trial cannot be addressed to them
                            </p>
                        )}
                        {serverError && <p className="text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2">{serverError}</p>}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button variant="plain" color="neutral" onClick={handleClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <button
                        disabled={!canSubmit}
                        onClick={() => void handleSubmit()}
                        className="h-9 px-4 text-xs font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 transition-colors inline-flex items-center gap-1.5"
                    >
                        {submitting ? <CircularProgress size="sm" sx={{ '--CircularProgress-size': '12px' }} /> : 'Grant Trial'}
                    </button>
                </DialogActions>
            </ModalDialog>
        </Modal>
    )
}
